// @ts-strict-ignore
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  Box,
  Button,
  NoData,
  NoResults,
  Pagination,
  RequestError,
  RowsSkeletonLoader,
  Box as Wrapper,
} from '@node-space/ui-components'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import FilterBar from 'components/FilterBar'
import { PageHeader } from 'components/header/PageHeader'
import { PathNames } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useChannelsQuery } from 'hooks/queries'
import { useApiPaginationWithPaymentsList } from 'hooks/useApiPaginationWithPaymentsList'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useMounted } from 'hooks/useMounted'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ChannelPayment } from 'types/channels'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { ChannelsHeaderButtons } from './components/ChannelsHeaderButtons'
import Table from './components/Table'
import useChannelsApi from './hooks/useChannelsApi'

const Channels = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const isMounted = useMounted()
  const [, setWalletAction] = useContext(WalletActionContext)
  const permissions = useMappedRoles()?.channels
  const isEditDisabled = !permissions?.canEdit

  const urlParams = new URLSearchParams(window.location.search)
  const queryPaymentsLinkBy = urlParams.get('dlt-reference') ?? null
  const isInitialized = useRef(false)

  const {
    executor,
    isLoading,
    isError,
    data: {
      paymentsList: { transactions, paging, pagingMax },
      search,
      isExportingCSV,
      dateValue,
    },
    actions: {
      onDateSelected,
      handleExportClicked,
      onPaymentStatusChanged,
      onPaymentTagChange,
      onMerchantSelected,
      showSearchResult,
      onResetAll,
      setPaymentsList,
      onPageChange,
      onSearchChanged,
    },
  } = useChannelsApi()

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useApiPaginationWithPaymentsList({
      onPageChange,
      setPaymentsList,
      executor,
      pagingMax,
    })

  const handleOpenChannelPaymentSheet = () => {
    setWalletAction({
      actionId: ActionIds?.CHANNEL_PAYMENT,
      header: t('payments.channelPayments.contactStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        onPaymentChannelSuccess: () =>
          void navigate(`${PathNames.SETTINGS}${PathNames.MANAGE_CHANNELS}`),
      },
    })
    setIsSheetOpen(true)
  }

  useEffect(() => {
    return () => {
      onResetAll()
    }
  }, [])

  useEffect(() => {
    if (queryPaymentsLinkBy && isMounted && !isLoading && isInitialized?.current) {
      onSearchChanged(queryPaymentsLinkBy)
    }
  }, [isInitialized?.current])

  const {
    data: channelsResp,
    isPending: isLoadingChannels,
    refetch: refetchChannels,
  } = useChannelsQuery({
    max: 1, // we only need to know if there are any channels
    offset: 0,
    merchant: 'ALL',
  })

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.CHANNELS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })

    if (isInitialized?.current) {
      refetchChannels()
    }
  }, [refetchChannels, isInitialized?.current])

  const isChannelsListEmpty = useMemo(() => {
    return (
      !isLoading && !isLoadingChannels && !transactions?.length && !channelsResp?.channels?.length
    )
  }, [isLoading, transactions, channelsResp, isLoadingChannels])

  const isTransactionsListEmpty = useMemo(() => {
    return !isLoading && !transactions?.length && !search
  }, [isLoading, transactions, search])

  return (
    <Wrapper className="channels-container">
      {!isChannelsListEmpty && (
        <>
          <PageHeader
            description={t('channelPaymentsIntroText')}
            renderRightSection={<ChannelsHeaderButtons />}
          />

          <FilterBar
            isChannel={true}
            eventHandlers={{
              executor,
              onMerchantSelected,
              onDateFilterChange: onDateSelected,
              onPaymentStatusChanged,
              onPaymentTagChange,
              handleExportClicked,
              onResetAll,
              showSearchResult,
            }}
            conditions={{ isExportingCSV }}
            filterDateRange={dateValue}
            searchInputPlaceholder={t('channelsSearch')}
          />
        </>
      )}

      {isLoading && (
        <Box paddingT={20}>
          <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
        </Box>
      )}

      {isError && <RequestError />}
      {isChannelsListEmpty && !isError && (
        <NoData
          dataName="channels"
          description={
            <>
              {t('noChannelsCreateChannel')}{' '}
              <ExternalLink href={process.env.CHANNELS_LEARN_MORE}>{t('learnMore')}</ExternalLink>
            </>
          }
          PrimaryButton={
            !isEditDisabled && (
              <Button onClick={() => handleOpenChannelPaymentSheet()}>
                {t('payments.channels.createChannel')}
              </Button>
            )
          }
        />
      )}

      {!search && isTransactionsListEmpty && !isChannelsListEmpty && !isError && (
        <NoData
          dataName="transactions"
          description={t('payments.emptyStateCryptoPaymentsDescription')}
        />
      )}

      {search && !isLoading && !transactions?.length && <NoResults onClear={onResetAll} />}

      {!isLoading && !!transactions?.length && !isError && (
        <Table channelPayments={transactions as unknown as ChannelPayment[]} />
      )}

      {!isError && !!paging?.total && (
        <Pagination
          testId="channels-pagination"
          page={page}
          rowsPerPage={rowsPerPage}
          total={paging.total}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
    </Wrapper>
  )
}

export default React.memo(Channels)
